import React, { useContext } from 'react';
import { Query } from '@apollo/react-components';
import { PROJECT_ACTORS } from '../../common/graphql/gql';
import type {
	GeneratedProjectActorsQuery as ProjectActorsQuery,
	GeneratedProjectActorInputs as ProjectActorInputs,
	GeneratedProjectActorsQueryVariables as ProjectActorsQueryVariables,
} from '../../common/graphql/types';
import type { ActorFilterProps, ActorsDataOperation } from '../../common/types';
import MainProviderContext from '../access-page-context';
import type {
	ActorServiceProps,
	ActorsServiceInternalProps,
	ActorWithoutRoleDescription,
} from './types';
import {
	removeActorFromData,
	updateActorFromData,
	getActorsFromQuery,
	getRenderPropsDataWithoutActors,
	getIsLastBatch,
} from './utils';

const getMappedParamToActualQuery = (param: ActorFilterProps): ProjectActorInputs => {
	let queryParam: ProjectActorInputs = { page: {} };
	if (param.searchText) {
		queryParam = { ...queryParam, ...{ search: { contains: param.searchText } } };
	}
	if (param.orderField || param.orderDirection) {
		// Both should be present to sort otherwise we will assume default orderDirection to be ASC and if field missing we will don't use it at all
		if (param.orderField) {
			queryParam = {
				...queryParam,
				...{
					orderBy: { field: param.orderField, direction: param.orderDirection || 'ASC' },
				},
			};
		}
	}
	if (param.roleIds || param.roleType) {
		queryParam = {
			...queryParam,
			...{
				roles: {
					ids: param.roleIds || [],
					actorTypes: param.roleType,
				},
			},
		};
	}
	if (param.pageNumber) {
		queryParam = {
			...queryParam,
			...{
				page: {
					number: param.pageNumber,
					size: param.pageSize,
				},
			},
		};
	}

	return queryParam;
};

const update = (
	queryData: ProjectActorsQuery,
	operation: ActorsDataOperation,
	id: string,
	roles?: ActorFilterProps['roleIds'],
): ProjectActorsQuery => {
	// operation can only be one of 'REMOVE_ACTOR' | 'UPDATE_ROLE'
	switch (operation) {
		case 'REMOVE_ACTOR':
			return removeActorFromData(queryData, { id, roles });
		case 'UPDATE_ROLE':
			return updateActorFromData(queryData, { id, roles });
		default:
			return { projectActors: null };
	}
};

export const ActorsInternal = (props: ActorsServiceInternalProps) => {
	const { data, children } = props;
	const { updateQuery } = data;
	const onDataChange = (
		operation: ActorsDataOperation,
		id: string,
		roles?: ActorFilterProps['roleIds'],
	) => {
		updateQuery(
			(prevResult: ProjectActorsQuery): ProjectActorsQuery =>
				update(prevResult, operation, id, roles),
		);
	};

	const actors: ActorWithoutRoleDescription[] = getActorsFromQuery(data.data);
	const isLastBatch = getIsLastBatch(data.data);

	return children({
		...getRenderPropsDataWithoutActors(data),
		actors,
		onDataChange,
		isLastBatch,
	});
};

const ActorsService = (props: ActorServiceProps) => {
	const { filters } = props;
	const { projectId } = useContext(MainProviderContext);
	const variables: ProjectActorsQueryVariables = {
		projectId,
		filter: filters ? getMappedParamToActualQuery(filters) : null,
	};
	return (
		<Query
			query={PROJECT_ACTORS}
			fetchPolicy="network-only"
			variables={variables}
			notifyOnNetworkStatusChange
			errorPolicy="all"
		>
			{/* @ts-expect-error - TS7006 - Parameter 'data' implicitly has an 'any' type. */}
			{(data) => <ActorsInternal data={data} {...props} />}
		</Query>
	);
};

export default ActorsService;
